import Login from "./pages/login/Login";
import NotFound from "./pages/notfound/NotFound";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";

import "./styles/dark.scss";
import { Suspense, useEffect, useState } from "react";
import "./app.scss";
import { useSelector } from "react-redux";
import ProtectedRedirect from "./utils/ProtectedRedirect";
import ProtectedRoute from "./utils/ProtectedRoute";
import PlainLayout from "./components/layouts/PlainLayout";
import MainLayout from "./components/layouts/MainLayout";
import Profile from "./pages/profile/Profile";
import { ownerRoutes } from "./routes/ownerRoutes";
import { adminRoutes } from "./routes/adminRoutes";
import { teacherRoutes } from "./routes/teacherRoutes";
import CircularProgressBox from "./components/CircularProgressBox";
import RouteWithTitle from "./components/RouteWithTitle";
import { receptionistRoutes } from "./routes/receptionistRoutes";
import { learnerRoutes } from "./routes/learnerRoutes";
import { fallbackRoutes } from "./routes/fallbackRoutes";
import { refreshToken } from "./services/authService";

function App() {
  const { darkMode } = useSelector((state) => state.theme);
  const { currentUser } = useSelector((state) => state.user);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    const checkRefresh = async () => {
      await refreshToken();
      setChecked(true); // ✅ mark refresh attempt complete
    };
    checkRefresh();
  }, []);

  if (!checked) {
    return <CircularProgressBox />; // ✅ show loader until refresh attempt finishes
  }

  return (
    <div className={darkMode ? "app dark" : "app"}>
      <Router>
        <Suspense fallback={<CircularProgressBox />}>
          <Routes>
            <Route path="/" element={<ProtectedRedirect />} />

            <Route element={<MainLayout />}>
              {ownerRoutes.map(
                ({ path, element, title, description, allowedRoles, requiredFeature, requiredPermission, requiredAnyPermission }) => (
                  <Route
                    key={path}
                    path={path}
                    element={
                      <ProtectedRoute
                        allowedRoles={allowedRoles || ["appOwner"]}
                        requiredFeature={requiredFeature}
                        requiredPermission={requiredPermission}
                        requiredAnyPermission={requiredAnyPermission}
                      >
                        <RouteWithTitle
                          element={element}
                          title={title}
                          description={description}
                        />
                      </ProtectedRoute>
                    }
                  />
                ),
              )}

              {adminRoutes.map(
                ({ path, element, title, description, allowedRoles, requiredFeature, requiredPermission, requiredAnyPermission }) => (
                  <Route
                    key={path}
                    path={path}
                    element={
                      <ProtectedRoute
                        allowedRoles={allowedRoles || ["admin"]}
                        requiredFeature={requiredFeature}
                        requiredPermission={requiredPermission}
                        requiredAnyPermission={requiredAnyPermission}
                      >
                        <RouteWithTitle
                          element={element}
                          title={title}
                          description={description}
                        />
                      </ProtectedRoute>
                    }
                  />
                ),
              )}
              {teacherRoutes.map(({ path, element, title, description, requiredFeature, requiredPermission, requiredAnyPermission }) => (
                <Route
                  key={path}
                  path={path}
                  element={
                    <ProtectedRoute
                      allowedRoles={["teacher"]}
                      requiredFeature={requiredFeature}
                      requiredPermission={requiredPermission}
                      requiredAnyPermission={requiredAnyPermission}
                    >
                      <RouteWithTitle
                        element={element}
                        title={title}
                        description={description}
                      />
                    </ProtectedRoute>
                  }
                />
              ))}
              {receptionistRoutes.map(
                ({ path, element, title, description, requiredFeature, requiredPermission, requiredAnyPermission }) => (
                  <Route
                    key={path}
                    path={path}
                    element={
                      <ProtectedRoute
                        allowedRoles={["receptionist"]}
                        requiredFeature={requiredFeature}
                        requiredPermission={requiredPermission}
                        requiredAnyPermission={requiredAnyPermission}
                      >
                        <RouteWithTitle
                          element={element}
                          title={title}
                          description={description}
                        />
                      </ProtectedRoute>
                    }
                  />
                ),
              )}

              {learnerRoutes.map(
                ({ path, element, title, description, allowedRoles }) => (
                  <Route
                    key={path}
                    path={path}
                    element={
                      <ProtectedRoute allowedRoles={allowedRoles}>
                        <RouteWithTitle
                          element={element}
                          title={title}
                          description={description}
                        />
                      </ProtectedRoute>
                    }
                  />
                ),
              )}

              {fallbackRoutes.map(
                ({ path, element, title, description, allowedRoles }) => (
                  <Route
                    key={path}
                    path={path}
                    element={
                      <ProtectedRoute allowedRoles={allowedRoles}>
                        <RouteWithTitle
                          element={element}
                          title={title}
                          description={description}
                        />
                      </ProtectedRoute>
                    }
                  />
                ),
              )}

              <Route
                path="/profile"
                element={
                  <ProtectedRoute
                    allowedRoles={[
                      "appOwner",
                      "admin",
                      "teacher",
                      "receptionist",
                      "parent",
                      "student",
                      "principal",
                      "manager",
                      "coordinator",
                    ]}
                  >
                    <RouteWithTitle
                      element={<Profile />}
                      title="Profile"
                      description="View and edit your profile information"
                    />
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* Public Routes without Navbar & Footer */}
            <Route element={<PlainLayout />}>
              <Route
                path="/login"
                element={
                  currentUser ? (
                    <Navigate to="/" />
                  ) : (
                    <RouteWithTitle
                      element={<Login />}
                      title="Login"
                      description="Access your account securely"
                    />
                  )
                }
              />
              <Route
                path="*"
                element={
                  <RouteWithTitle
                    element={<NotFound />}
                    title="Page Not Found"
                    description="The page you are looking for does not exist"
                  />
                }
              />
            </Route>
          </Routes>
        </Suspense>
      </Router>
    </div>
  );
}

export default App;
